import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { ScrollText, Plus, Pencil, Trash2, ExternalLink } from "lucide-react";

// Map of action → human-readable label + accent colour.
const ACTION_META = {
  "voucher.create": { label: "Created",  icon: Plus,    cls: "text-emerald-700 bg-emerald-50" },
  "voucher.update": { label: "Updated",  icon: Pencil,  cls: "text-blue-700 bg-blue-50" },
  "voucher.delete": { label: "Deleted",  icon: Trash2,  cls: "text-rose-700 bg-rose-50" },
};

function fmtAt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function Inner() {
  const { activeId, active } = useCompany();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    const params = {};
    if (actionFilter) params.action = actionFilter;
    api.get(`/companies/${activeId}/audit-log`, { params })
      .then((r) => { setEntries(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [activeId, actionFilter]);

  const filtered = useMemo(() => {
    if (!q) return entries;
    const needle = q.toLowerCase();
    return entries.filter((e) =>
      (e.user_email || "").toLowerCase().includes(needle) ||
      (e.user_name || "").toLowerCase().includes(needle) ||
      JSON.stringify(e.meta || {}).toLowerCase().includes(needle)
    );
  }, [entries, q]);

  return (
    <>
      <PageHeader
        title="Audit Trail"
        subtitle={`${active?.name || ""} • Who did what, and when`}
      />
      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="h-8 rounded border border-gray-300 px-2 text-sm"
            data-testid="select-audit-filter"
          >
            <option value="">All actions</option>
            <option value="voucher.create">Voucher created</option>
            <option value="voucher.update">Voucher updated</option>
            <option value="voucher.delete">Voucher deleted</option>
          </select>
          <Input
            placeholder="Search user / email / voucher no…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-8 max-w-sm"
            data-testid="input-audit-search"
          />
          <span className="ml-auto text-xs text-gray-500 font-mono">{filtered.length} entries</span>
        </div>

        <div className="border border-gray-200 rounded bg-white overflow-hidden" data-testid="audit-log-table">
          <table className="tx-table">
            <thead>
              <tr>
                <th>When</th><th>User</th><th>Action</th><th>Resource</th><th>Details</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-8 text-sm text-gray-400">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-sm text-gray-400">
                  No audit entries match this filter
                </td></tr>
              )}
              {filtered.map((e) => {
                const meta = ACTION_META[e.action] || { label: e.action, icon: ScrollText, cls: "text-gray-700 bg-gray-50" };
                const Icon = meta.icon;
                const vno = e.meta?.voucher_no;
                const total = e.meta?.total;
                const dateOf = e.meta?.date;
                const vtype = e.meta?.type;
                const diff = e.meta?.from && e.meta?.to;
                return (
                  <tr key={e.id} data-testid={`audit-row-${e.id}`}>
                    <td className="font-mono text-xs whitespace-nowrap">{fmtAt(e.at)}</td>
                    <td>
                      <div className="text-sm font-medium">{e.user_name || "—"}</div>
                      <div className="text-[11px] text-gray-500 font-mono">{e.user_email}</div>
                    </td>
                    <td>
                      <span className={`text-xs px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${meta.cls}`}>
                        <Icon className="w-3 h-3" />{meta.label}
                      </span>
                    </td>
                    <td>
                      <div className="text-xs uppercase tracking-wider text-gray-500">{e.resource}</div>
                      {vno && <div className="font-mono">{vno}</div>}
                    </td>
                    <td className="max-w-md">
                      {vtype && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 mr-1.5">{vtype}</span>}
                      {dateOf && <span className="text-xs text-gray-500 font-mono mr-2">{dateOf}</span>}
                      {typeof total === "number" && <span className="font-mono text-xs">₹{formatINR(total)}</span>}
                      {diff && (
                        <div className="text-[11px] text-gray-600 mt-0.5">
                          {e.meta.from.total !== e.meta.to.total && (
                            <span>
                              total: <span className="font-mono text-rose-600">₹{formatINR(e.meta.from.total || 0)}</span>
                              {" → "}
                              <span className="font-mono text-emerald-600">₹{formatINR(e.meta.to.total || 0)}</span>
                            </span>
                          )}
                          {e.meta.from.date !== e.meta.to.date && (
                            <span className="ml-3">
                              date: <span className="font-mono">{e.meta.from.date}</span>
                              {" → "}
                              <span className="font-mono">{e.meta.to.date}</span>
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {e.action !== "voucher.delete" && e.resource === "voucher" && (
                        <Link
                          to={`/vouchers/edit/${e.resource_id}`}
                          className="text-gray-500 hover:text-blue-700 inline-flex items-center gap-1 text-xs"
                          data-testid={`audit-open-${e.resource_id}`}
                          title="Open this voucher"
                        >
                          Open <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default function AuditLog() {
  return <RequireCompany><Inner /></RequireCompany>;
}
